import { Alert, Box, Button, Chip, CircularProgress, Container, Stack } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePublicProfilePage } from './PublicProfilePage.hooks';
import ProfileHero from './components/ProfileHero';
import ProfileActions from './components/ProfileActions';
import ProfileServices from './components/ProfileServices';
import ProfileBio from './components/ProfileBio';
import CustomerLoginDialog from './components/CustomerLoginDialog';
import SetPinDialog from './components/SetPinDialog';
import { MyVisitsList } from '@features/customer-reservations/components/MyVisitsList';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { FormattedMessage, useIntl } from 'react-intl';

const PRIMARY = '#D02964';

const PublicProfilePage = () => {
  const intl = useIntl();
  const {
    profile,
    loading,
    error,
    mapsUrl,
    isAuthForProfile,
    authLoading,
    logout,
    displayServices,
    myVisits,
    hasPinSet,
    checkSession,
  } = usePublicProfilePage();
  const navigate = useNavigate();
  const [loginOpen, setLoginOpen] = useState(false);

  if (loading || authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100dvh">
        <CircularProgress sx={{ color: '#D02964' }} />
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">
          {error ?? intl.formatMessage({ id: 'publicProfile.notFound' })}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100dvh' }}>
      <ProfileHero
        name={profile.name}
        avatarUrl={profile.avatarUrl}
        address={profile.address}
        mapsUrl={mapsUrl}
      />

      <Container maxWidth="sm" sx={{ pt: 2, pb: 4 }}>
        {isAuthForProfile ? (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: '12px',
              bgcolor: `${PRIMARY}10`,
              border: `1px solid ${PRIMARY}30`,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonIcon sx={{ fontSize: 18, color: PRIMARY }} />
                <Chip
                  label={intl.formatMessage({ id: 'publicProfile.loggedInAsRegular' })}
                  size="small"
                  sx={{ bgcolor: `${PRIMARY}20`, color: PRIMARY, fontWeight: 600 }}
                />
              </Stack>
              <Button
                size="small"
                startIcon={<LogoutIcon />}
                onClick={async () => {
                  await logout();
                }}
                sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
              >
                <FormattedMessage id="common.logout" />
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PersonIcon />}
              onClick={() => setLoginOpen(true)}
              sx={{
                borderColor: PRIMARY,
                color: PRIMARY,
                borderRadius: '10px',
                fontSize: '0.8rem',
              }}
            >
              <FormattedMessage id="publicProfile.loginAsRegular" />
            </Button>
          </Box>
        )}

        <ProfileActions
          onBook={() => navigate('booking', { relative: 'path' })}
          phone={profile.phone}
          email={profile.email}
          allowPublicReservations={profile.allowPublicReservations || isAuthForProfile}
        />
        {isAuthForProfile && myVisits.length > 0 && (
          <Box sx={{ my: 3 }}>
            <MyVisitsList visits={myVisits} />
          </Box>
        )}
        {profile.bio && <ProfileBio bio={profile.bio} />}

        <ProfileServices
          services={displayServices}
          preferredCurrency={profile.preferredCurrency}
          onSelect={
            isAuthForProfile
              ? service =>
                  navigate(`booking?${new URLSearchParams({ serviceId: service.id })}`, {
                    relative: 'path',
                  })
              : undefined
          }
        />
      </Container>

      <CustomerLoginDialog
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        profileUserId={profile.userId}
      />

      <SetPinDialog
        open={isAuthForProfile && !hasPinSet}
        onSuccess={async () => {
          await checkSession();
        }}
      />
    </Box>
  );
};

export default PublicProfilePage;
